import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, CheckCircle2, Clock } from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import './Notices.css';

// Simple date formatter (Matches your Kotlin logic of taking the first 10 chars)
const formatNoticeDate = (dateString) => {
  try {
    return dateString.substring(0, 10);
  } catch (e) {
    return "Recent";
  }
};

const normaliseNotice = (notice, studentId) => ({
  ...notice,
  // Some responses expose isRead directly, while others return the readBy
  // student-id list. Support both response shapes.
  isRead: Boolean(
    notice.isRead ?? notice.readBy?.some((id) => String(id) === String(studentId))
  ),
});

// ---------------- NOTICE CARD COMPONENT ----------------
const NoticeCard = ({ notice, studentId }) => {
  const [isRead, setIsRead] = useState(notice.isRead);

  const handleCardClick = async () => {
    if (isRead) return;
    
    // Optimistic UI update for instant feedback
    setIsRead(true);
    
    try {
      await ApiService.markNoticeRead({ 
        noticeNo: notice.noticeNo, 
        studentId: studentId 
      });
    } catch (error) {
      // Revert state if API fails
      setIsRead(false);
      console.error("Failed to mark notice as read", error);
    }
  };

  return (
    <div 
      className={`notice-card ${!isRead ? 'unread' : 'read'}`}
      onClick={handleCardClick}
    >
      <div className="notice-header">
        <div className="notice-title-row">
          <div className="notice-icon-wrapper">
            {isRead ? (
              <CheckCircle2 size={16} color="var(--text-light)" />
            ) : (
              <Bell size={16} color="white" fill="white" />
            )}
          </div>
          <h3 className="notice-title">{notice.title}</h3>
        </div>
        
        {!isRead && <span className="new-badge">NEW</span>}
      </div>

      <p className="notice-message">{notice.message}</p>

      <div className="notice-footer">
        <Clock size={14} color="var(--text-light)" />
        <span className="notice-date">{formatNoticeDate(notice.createdAt)}</span>
      </div>
    </div>
  );
};

// ---------------- MAIN NOTICE SCREEN ----------------
const Notices = () => {
  const navigate = useNavigate();
  const { studentId } = useAuth();
  
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchNotices = async () => {
      if (!studentId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        // This endpoint returns the notices for this student, including the
        // read state. The deployed /api/notice/all endpoint returns 404.
        const response = await ApiService.getStudentNotices(studentId);
        const noticeList = Array.isArray(response.data)
          ? response.data
          : response.data?.notices || [];

        if (Array.isArray(noticeList)) {
          // Sort by newest first
          const sorted = noticeList.map((notice) => normaliseNotice(notice, studentId)).sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          setNotices(sorted);
        }
      } catch (error) {
        console.error("Failed to fetch notices", error);
        setError('Could not load notices. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, [studentId]);

  return (
    <div className="notices-screen">
      {/* Top Bar */}
      <div className="top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
        <h2 className="top-bar-title">Notices</h2>
        <div style={{ width: 24 }}></div> {/* Spacer for centering */}
      </div>

      <div className="notices-content">
        {loading ? (
          <div className="center-wrapper">
            <div className="spinner"></div>
          </div>
        ) : error ? (
          <div className="empty-state" role="alert">
            <BellOff size={80} color="var(--text-light, #94a3b8)" />
            <h3 className="empty-title">Unable to load notices</h3>
            <p className="empty-subtitle">{error}</p>
          </div>
        ) : notices.length === 0 ? (
          <div className="empty-state">
            <BellOff size={80} color="var(--text-light, #94a3b8)" />
            <h3 className="empty-title">No new notices</h3>
            <p className="empty-subtitle">We'll let you know when updates arrive.</p>
          </div>
        ) : (
          <div className="notices-list">
            {notices.map(notice => (
              <NoticeCard 
                key={notice.noticeNo} 
                notice={notice} 
                studentId={studentId} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notices;
